/**
 * Apply store geom position/orientation to Three.js objects under a body's pose group.
 * Full scene rebuilds already set these; this keeps multiple geoms in sync when only store
 * transforms change (e.g. paired visual/collision, inspector offsets, gizmos).
 *
 * Mesh geoms use split transforms: `outer` (position) + `geom` (quaternion). Primitives use
 * a single group. Leaf meshes and ref/meshEditor layers are skipped.
 *
 * @param {THREE.Object3D | null | undefined} poseGroup
 * @param {Record<string, { geoms?: unknown[] }> | null | undefined} bodiesRecord
 */
export function syncGeomTransformsForBodyPoseGroup(poseGroup, bodiesRecord) {
  if (!poseGroup || !bodiesRecord) return;
  const bodyUuid = poseGroup.userData?.bodyUuid;
  if (!bodyUuid) return;
  const body = bodiesRecord[bodyUuid];
  if (!body || !Array.isArray(body.geoms)) return;

  const geomByUuid = new Map();
  for (const g of body.geoms) {
    if (g && g.uuid) {
      geomByUuid.set(String(g.uuid), g);
    }
  }

  poseGroup.traverse((obj) => {
    const geomUuid = obj.userData?.geomUuid;
    if (!geomUuid) return;
    const geom = geomByUuid.get(String(geomUuid));
    if (!geom) return;

    const layer = obj.userData?.transformLayer;
    if (layer === "ref" || layer === "meshEditor") return;

    const pos = Array.isArray(geom.position) ? geom.position : [0, 0, 0];
    const ori = Array.isArray(geom.orientation) ? geom.orientation : [1, 0, 0, 0];
    const px = Number(pos[0]) || 0;
    const py = Number(pos[1]) || 0;
    const pz = Number(pos[2]) || 0;
    const qwRaw = Number(ori[0]);
    const qx = Number(ori[1]) || 0;
    const qy = Number(ori[2]) || 0;
    const qz = Number(ori[3]) || 0;
    const qw = Number.isFinite(qwRaw) ? qwRaw : 1;

    if (layer === "outer") {
      obj.position.set(px, py, pz);
      return;
    }
    if (layer === "geom") {
      obj.quaternion.set(qx, qy, qz, qw);
      obj.quaternion.normalize();
      return;
    }

    if (obj.isMesh) return;

    const t = obj.userData?.type;
    if (t === "visual" || t === "collision") {
      obj.position.set(px, py, pz);
      obj.quaternion.set(qx, qy, qz, qw);
      obj.quaternion.normalize();
    }
  });
}
